// Kanzleivergleich: ComeFit-Parallelbuchhaltung (intern, vorlaeufig) gegen die Auswertungen des Steuerberaters (maßgeblich).
// Es wird nur verglichen und nachvollzogen - nichts angeglichen, nichts gesendet. Ein Vergleich wird NIE als vollstaendig oder korrekt dargestellt, wenn wesentliche Daten fehlen.
import { useState, useEffect, useCallback, useMemo } from "react";
import THEME from "../../../../theme";
import { fs } from "../../utils/styles";
import { pbApi } from "../../../belege/belegeApi";
import { Meldung, Chip, useAktion } from "../../../belege/ui";
import { Seitenkopf, PbBanner, Kachel, kachelRaster, Abschnitt, Pille, geld, useBreite, BREAKPOINT_KLEIN } from "../../ui/Bausteine";

const ymText = (ym) => `${String(ym).slice(4)}/${String(ym).slice(0, 4)}`;
const ymWert = (ym) => `${String(ym).slice(0, 4)}-${String(ym).slice(4)}`;

export const STATUS = {
  gleich: { text: "Übereinstimmend", farbe: THEME.colors.success, rang: 5, gruppe: "gleich" },
  abweichend: { text: "Abweichend", farbe: THEME.colors.danger, rang: 0, gruppe: "abweichend" },
  nur_pb: { text: "Nur ComeFit", farbe: THEME.colors.warning, rang: 1, gruppe: "nur_pb" },
  nur_kanzlei: { text: "Nur Kanzlei", farbe: THEME.colors.warning, rang: 2, gruppe: "nur_kanzlei" },
  kanzlei_unklar: { text: "Nicht vergleichbar (Kanzlei-Zeile unklar)", farbe: THEME.colors.textMuted, rang: 3, gruppe: "nicht_vergleichbar" },
  nicht_vergleichbar: { text: "Nicht vergleichbar", farbe: THEME.colors.textMuted, rang: 4, gruppe: "nicht_vergleichbar" },
};
const FILTER = [["alle", "Alle"], ["gleich", "Übereinstimmend"], ["abweichend", "Abweichend"], ["nur_pb", "Nur ComeFit"], ["nur_kanzlei", "Nur Kanzlei"], ["nicht_vergleichbar", "Nicht vergleichbar"]];
const KLASSE = { vergleichbar: "vergleichbar", systembedingt: "systembedingt (Personen-/Sammelkonten)", kanzlei_vorbehalten: "bleibt bei der Kanzlei" };
const URSACHE = { zeitversatz: "Zeitversatz", kanzlei_buchung: "Buchung der Kanzlei", beleg_fehlt: "Beleg fehlt", zuordnung: "Zuordnung/Kontierung", steuer: "Steuer", privat: "Privat", kreditoren: "Kreditoren", sonstiges: "Sonstiges" };
const ART = [["konto", "Konten (Bewegung im Monat)"], ["saldo", "Salden der Bestandskonten"], ["bwa", "BWA-Positionen"]];

// Aussagekraft des Vergleichs: zaehlt auf, was fehlt oder einschraenkt. Es gibt bewusst KEIN "vollstaendig" oder "korrekt".
export function aussagekraft(v) {
  if (!v) return null;
  const z = v.zusammenfassung;
  const st = z.status || {};
  const punkte = [];
  const harte = [];
  if (!v.methode) harte.push("Die Buchungsmethode (EÜR/Bilanz, Ist/Soll) ist nicht festgelegt – die Parallelbuchhaltung bucht nichts.");
  if (z.pb_buchungen_im_monat === 0) harte.push("In diesem Monat gibt es keine Buchungen der Parallelbuchhaltung – alle Kanzlei-Konten erscheinen als „nur Kanzlei“.");
  if (z.ohne_anfangssalden) punkte.push("Anfangssalden fehlen: Die Salden der Bestandskonten werden nicht verglichen (nur Monatsbewegungen).");
  if (Number(z.unsicher_summe) > 0) punkte.push(`Enthält ${geld(z.unsicher_summe)} aus unsicheren Buchungen (nicht als korrekt zu betrachten).`);
  if ((st.kanzlei_unklar || 0) > 0) punkte.push(`${st.kanzlei_unklar} Kanzlei-Zeile(n) im PDF-Import unklar zerlegt – nicht verglichen, nichts geraten.`);
  if (z.kanzlei_selbstpruefung_passt === false) punkte.push("Die Kanzlei-BWA lässt sich aus dem importierten SuSa nicht vollständig nachrechnen – Differenzen können am Import liegen.");
  if ((st.nicht_vergleichbar || 0) > 0) punkte.push(`${st.nicht_vergleichbar} Konten sind bewusst nicht vergleichbar (Personen-/Sammelkonten, Lohn, Abschreibung, Sonderbuchungen). Unterschiedliche Buchungsmethoden (z. B. Kreditoren: Kanzlei über Lieferantenkonten, ComeFit bei Zahlung) sind möglich.`);
  if ((st.nur_kanzlei || 0) > 0) punkte.push(`${st.nur_kanzlei} Konto/Konten haben nur bei der Kanzlei Bewegungen.`);
  const stufe = harte.length ? "nicht_belastbar" : (punkte.length ? "eingeschraenkt" : "alle_daten");
  return { stufe, punkte: [...harte, ...punkte], harte };
}

const STUFE = {
  nicht_belastbar: ["NICHT AUSSAGEKRÄFTIG", THEME.colors.danger],
  eingeschraenkt: ["EINGESCHRÄNKT AUSSAGEKRÄFTIG", THEME.colors.warning],
  alle_daten: ["Alle Daten vorhanden – Abgleich mit dem Steuerberater bleibt erforderlich", THEME.colors.accent],
};

export function MonatsAuswahl({ monate, verlauf, ym, onWaehle }) {
  const breite = useBreite();
  if (breite < BREAKPOINT_KLEIN) {
    return (
      <label style={{ ...fs.label, margin: "0 0 12px" }}>Monat
        <select style={{ ...fs.input, width: "100%", marginTop: 4 }} value={ym || ""} onChange={e => onWaehle(Number(e.target.value))}>
          {monate.map(m => <option key={m} value={m}>{ymText(m)}</option>)}
        </select>
      </label>
    );
  }
  const info = Object.fromEntries((verlauf || []).map(x => [x.ym, x]));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))", gap: 6, marginBottom: 14 }} aria-label="Monat wählen">
      {monate.map(m => {
        const x = info[m];
        return (
          <button key={m} onClick={() => onWaehle(m)} aria-pressed={ym === m} style={{ textAlign: "left", cursor: "pointer", background: ym === m ? THEME.colors.surfaceMuted : THEME.colors.surface, color: THEME.colors.text,
            border: `1px solid ${ym === m ? THEME.colors.primary : THEME.colors.border}`, borderRadius: THEME.radius.md, padding: "6px 10px" }}>
            <strong>{ymText(m)}</strong>
            <div style={{ fontSize: THEME.fontSize.xs, color: x && x.pb_buchungen ? (x.abweichend ? THEME.colors.danger : THEME.colors.textSecondary) : THEME.colors.textMuted }}>
              {!x ? "…" : x.pb_buchungen ? `${x.gleich} gleich · ${x.abweichend} abw.` : "keine ComeFit-Buchungen"}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function useVergleichsDaten() {
  const [monate, setMonate] = useState(null);
  const [verlauf, setVerlauf] = useState([]);
  const [fehler, setFehler] = useState("");
  useEffect(() => {
    pbApi.kanzleiMonate().then(r => {
      setMonate(r.monate);
      if (r.monate.length) pbApi.verlauf(r.monate[0], r.monate[r.monate.length - 1]).then(setVerlauf).catch(() => {});
    }).catch(e => { setMonate([]); setFehler(e.message); });
  }, []);
  return { monate, verlauf, fehler, setFehler };
}

export default function Monatsvergleich({ modus = "vergleich" }) {
  const { monate, verlauf, fehler: fehlerStart } = useVergleichsDaten();
  const [ym, setYm] = useState(null);
  const [v, setV] = useState(null);
  const [laed, setLaed] = useState(false);
  const [art, setArt] = useState("konto");
  const [filter, setFilter] = useState(modus === "abweichungen" ? "offen_abweichungen" : "alle");
  const [suche, setSuche] = useState("");
  const [offen, setOffen] = useState(null);
  const [fehler, setFehler] = useState("");
  const [hinweis, setHinweis] = useState("");
  const [gespeichert, setGespeichert] = useState(null);
  const aktion = useAktion(setFehler, setHinweis);
  const breite = useBreite();

  useEffect(() => { if (monate && monate.length && !ym) setYm(monate[monate.length - 1]); }, [monate, ym]);
  const rechne = useCallback(() => {
    if (!ym) return;
    setLaed(true); setFehler("");
    pbApi.vergleich(ym).then(r => { setV(r); setLaed(false); }).catch(e => { setV(null); setLaed(false); setFehler(e.message); });
    pbApi.vergleichGespeichert(ym).then(g => setGespeichert(g.vergleich_id ? g : null)).catch(() => setGespeichert(null));
  }, [ym]);
  useEffect(() => { setOffen(null); rechne(); }, [rechne]);

  const akt = useMemo(() => aussagekraft(v), [v]);
  const zeilenAlle = useMemo(() => (v?.zeilen || []).filter(z => z.art === art), [v, art]);
  const zaehl = useMemo(() => {
    const c = { alle: zeilenAlle.length };
    zeilenAlle.forEach(z => { const g = STATUS[z.status]?.gruppe || z.status; c[g] = (c[g] || 0) + 1; });
    return c;
  }, [zeilenAlle]);
  const zeilen = useMemo(() => {
    const n = v?.notizen || {};
    return zeilenAlle.filter(z => {
      const g = STATUS[z.status]?.gruppe || z.status;
      if (filter === "offen_abweichungen") {
        const nz = n[`${z.art}:${z.schluessel}`];
        if (!["abweichend", "nur_pb", "nur_kanzlei"].includes(g) && z.status !== "kanzlei_unklar") return false;
        if (nz && ["erklaert", "kein_fehler"].includes(nz.status)) return false;
      } else if (filter !== "alle" && g !== filter) return false;
      if (suche && !(`${z.schluessel} ${z.bezeichnung || ""}`.toLowerCase().includes(suche.toLowerCase()))) return false;
      return true;
    }).sort((a, b) => (STATUS[a.status]?.rang ?? 9) - (STATUS[b.status]?.rang ?? 9) || a.schluessel.localeCompare(b.schluessel));
  }, [zeilenAlle, filter, suche, v]);

  if (monate === null) return <div>Lädt…</div>;
  const zus = v?.zusammenfassung;
  const ergebnis = (v?.zeilen || []).find(z => z.art === "bwa" && z.schluessel === "vorlaeufiges_ergebnis");
  const [stufeText, stufeFarbe] = akt ? STUFE[akt.stufe] : ["", ""];
  const diffFarbe = ergebnis && ergebnis.differenz !== null && Number(ergebnis.differenz) !== 0 ? THEME.colors.danger : THEME.colors.success;
  const titel = modus === "abweichungen" ? "Abweichungen" : "Monatsvergleich ComeFit ↔ Steuerberater";

  return (
    <div style={{ color: THEME.colors.text }}>
      <PbBanner text="Die Buchhaltung des Steuerberaters bleibt maßgeblich. Dieser Vergleich dient nur dem Nachvollziehen von Unterschieden; er korrigiert nichts und überträgt nichts." />
      <Seitenkopf titel={titel} untertitel={modus === "abweichungen" ? "Nur Zeilen, die noch nicht erklärt sind. Mit „erklärt“ oder „kein Fehler“ markierte Abweichungen verschwinden hier (bleiben im Monatsvergleich sichtbar)." : "Pro Monat: Ergebnis, Konten, Salden und BWA-Positionen im Vergleich."} />
      <Meldung fehler={fehler || fehlerStart} hinweis={hinweis} />
      {monate.length === 0 && <Abschnitt titel="Keine Kanzleidaten"><div style={{ color: THEME.colors.warning }}>Es sind noch keine Kanzlei-BWA/SuSa importiert (Steuerberater → Import Kanzlei-BWA und SuSa). Ohne Kanzleidaten ist kein Vergleich möglich.</div></Abschnitt>}
      {monate.length > 0 && <MonatsAuswahl monate={monate} verlauf={verlauf} ym={ym} onWaehle={setYm} />}
      {laed && <div style={{ color: THEME.colors.textMuted }}>Vergleich wird berechnet…</div>}

      {v && zus && akt && (
        <>
          <div style={kachelRaster}>
            <Kachel gross titel="Ergebnis laut ComeFit (intern, vorläufig)" wert={ergebnis ? geld(ergebnis.pb_wert) : "–"} farbe={THEME.colors.text}
              hinweis={Number(zus.unsicher_summe) > 0 ? `enthält ${geld(zus.unsicher_summe)} aus unsicheren Buchungen` : (zus.pb_buchungen_im_monat === 0 ? "keine Buchungen in diesem Monat" : `${zus.pb_buchungen_im_monat} Buchungen`)} />
            <Kachel gross titel="Ergebnis laut Steuerberater (maßgeblich)" wert={ergebnis ? geld(ergebnis.kanzlei_wert) : "nicht verfügbar"} farbe={THEME.colors.text}
              hinweis={ergebnis ? `Kanzlei-BWA ${ymText(ym)}` : "Keine Kanzlei-BWA für diesen Monat importiert"} />
            <Kachel gross titel="Differenz" wert={ergebnis && ergebnis.differenz !== null ? geld(ergebnis.differenz, true) : "–"} farbe={diffFarbe} rahmen={stufeFarbe}
              hinweis="ComeFit minus Steuerberater – nur aussagekräftig im Rahmen der unten genannten Einschränkungen" />
          </div>

          <div role="status" style={{ border: `1px solid ${stufeFarbe}`, borderLeft: `4px solid ${stufeFarbe}`, borderRadius: THEME.radius.md, padding: "10px 14px", marginBottom: 16, background: THEME.colors.surface }}>
            <strong style={{ color: stufeFarbe }}>{stufeText}</strong>
            {akt.punkte.length > 0 && <ul style={{ margin: "6px 0 0 18px", padding: 0, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>{akt.punkte.map((p, i) => <li key={i} style={{ marginBottom: 3 }}>{p}</li>)}</ul>}
            <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 6 }}>
              Methode ComeFit: {v.methode || "nicht festgelegt"} · Toleranz {geld(v.toleranz)} · Kanzlei-Import #{v.kanzlei_import_id}
              {zus.gleich_quote !== null && ` · ${zus.gleich_quote} % der vergleichbaren Konten übereinstimmend`}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
            <button style={fs.btn} onClick={() => aktion(() => pbApi.vergleichSpeichern(ym), "Vergleich gespeichert (älterer Stand als überholt markiert).").then(rechne)}>Vergleich speichern</button>
            {gespeichert && <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>zuletzt gespeichert {new Date(gespeichert.erstellt_am).toLocaleString("de-DE")}</span>}
          </div>

          <Abschnitt>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {ART.map(([k, t]) => <Pille key={k} text={t} aktiv={art === k} onClick={() => { setArt(k); setOffen(null); }} />)}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {modus === "abweichungen" && <Pille text="Noch zu klären" aktiv={filter === "offen_abweichungen"} onClick={() => setFilter("offen_abweichungen")} farbe={THEME.colors.danger} />}
              {FILTER.map(([k, t]) => <Pille key={k} text={t} zahl={zaehl[k] || 0} aktiv={filter === k} onClick={() => setFilter(k)} farbe={k === "gleich" ? THEME.colors.success : k === "abweichend" ? THEME.colors.danger : THEME.colors.warning} />)}
            </div>
            <input aria-label="Konto suchen" style={{ ...fs.input, width: "100%", maxWidth: 340, marginBottom: 12 }} placeholder="Konto oder Bezeichnung suchen" value={suche} onChange={e => setSuche(e.target.value)} />

            {zeilen.length === 0 && <div style={{ color: THEME.colors.textMuted }}>Keine Zeilen für diese Auswahl.</div>}
            {breite >= BREAKPOINT_KLEIN ? (
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <thead>
                  <tr>
                    <th style={{ ...fs.th, width: "11%" }}>{art === "bwa" ? "Position" : "SKR03-Konto"}</th><th style={{ ...fs.th, width: "27%" }}>Bezeichnung</th>
                    <th style={{ ...fs.th, width: "14%", textAlign: "right" }}>ComeFit</th><th style={{ ...fs.th, width: "14%", textAlign: "right" }}>Kanzlei</th>
                    <th style={{ ...fs.th, width: "14%", textAlign: "right" }}>Differenz</th><th style={{ ...fs.th, width: "20%" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map(z => <TabellenZeile key={`${z.art}:${z.schluessel}`} z={z} ym={ym} v={v} offen={offen === `${z.art}:${z.schluessel}`} onOffen={() => setOffen(offen === `${z.art}:${z.schluessel}` ? null : `${z.art}:${z.schluessel}`)} neuLaden={rechne} setFehler={setFehler} setHinweis={setHinweis} tabelle />)}
                </tbody>
              </table>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {zeilen.map(z => <TabellenZeile key={`${z.art}:${z.schluessel}`} z={z} ym={ym} v={v} offen={offen === `${z.art}:${z.schluessel}`} onOffen={() => setOffen(offen === `${z.art}:${z.schluessel}` ? null : `${z.art}:${z.schluessel}`)} neuLaden={rechne} setFehler={setFehler} setHinweis={setHinweis} />)}
              </div>
            )}
          </Abschnitt>
          <KlassifikationAbschnitt neuLaden={rechne} setFehler={setFehler} setHinweis={setHinweis} />
        </>
      )}
    </div>
  );
}

function wertZelle(w, farbe) { return <span style={{ color: farbe, fontVariantNumeric: "tabular-nums" }}>{w === null || w === undefined ? "–" : geld(w)}</span>; }

function TabellenZeile({ z, ym, v, offen, onOffen, neuLaden, setFehler, setHinweis, tabelle }) {
  const s = STATUS[z.status] || { text: z.status, farbe: THEME.colors.border };
  const nz = v.notizen[`${z.art}:${z.schluessel}`];
  const diffFarbe = z.differenz !== null && z.differenz !== undefined && Number(z.differenz) !== 0 ? (z.status === "nicht_vergleichbar" ? THEME.colors.textMuted : THEME.colors.danger) : THEME.colors.text;
  const kopf = (
    <>
      <span><strong>{z.schluessel}</strong></span>
      <span style={{ overflowWrap: "anywhere" }}>{z.bezeichnung || "–"}</span>
    </>
  );
  const badge = <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}><Chip text={s.text} farbe={s.farbe} />{nz && <Chip text={`Notiz: ${nz.status}`} farbe={THEME.colors.accent} />}</span>;
  if (tabelle) {
    return (
      <>
        <tr onClick={onOffen} tabIndex={0} onKeyDown={e => (e.key === "Enter" || e.key === " ") && onOffen()} style={{ cursor: "pointer", background: offen ? THEME.colors.surfaceMuted : "transparent" }} aria-expanded={offen}>
          <td style={{ ...fs.td, verticalAlign: "top" }}><strong>{z.schluessel}</strong></td>
          <td style={{ ...fs.td, verticalAlign: "top", overflowWrap: "anywhere" }}>{z.bezeichnung || "–"}</td>
          <td style={{ ...fs.td, textAlign: "right", verticalAlign: "top" }}>{wertZelle(z.pb_wert)}</td>
          <td style={{ ...fs.td, textAlign: "right", verticalAlign: "top" }}>{wertZelle(z.kanzlei_wert)}</td>
          <td style={{ ...fs.td, textAlign: "right", verticalAlign: "top" }}>{wertZelle(z.differenz, diffFarbe)}</td>
          <td style={{ ...fs.td, verticalAlign: "top" }}>{badge}</td>
        </tr>
        {offen && <tr><td colSpan={6} style={{ ...fs.td, background: THEME.colors.background }}><KontoDetail z={z} ym={ym} nz={nz} neuLaden={neuLaden} setFehler={setFehler} setHinweis={setHinweis} /></td></tr>}
      </>
    );
  }
  return (
    <div style={{ border: `1px solid ${THEME.colors.border}`, borderLeft: `4px solid ${s.farbe}`, borderRadius: THEME.radius.md, background: offen ? THEME.colors.surfaceMuted : THEME.colors.background, padding: "10px 12px" }}>
      <div role="button" tabIndex={0} aria-expanded={offen} onClick={onOffen} onKeyDown={e => (e.key === "Enter" || e.key === " ") && onOffen()} style={{ cursor: "pointer" }}>
        <div style={{ display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>{kopf}</div>
        <div style={{ margin: "6px 0" }}>{badge}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: THEME.fontSize.base }}>
          <span style={{ color: THEME.colors.textMuted }}>ComeFit</span><span style={{ textAlign: "right" }}>{wertZelle(z.pb_wert)}</span>
          <span style={{ color: THEME.colors.textMuted }}>Kanzlei</span><span style={{ textAlign: "right" }}>{wertZelle(z.kanzlei_wert)}</span>
          <span style={{ color: THEME.colors.textMuted }}>Differenz</span><span style={{ textAlign: "right" }}>{wertZelle(z.differenz, diffFarbe)}</span>
        </div>
      </div>
      {offen && <div style={{ marginTop: 10 }}><KontoDetail z={z} ym={ym} nz={nz} neuLaden={neuLaden} setFehler={setFehler} setHinweis={setHinweis} /></div>}
    </div>
  );
}

// Nachvollziehen einer Abweichung: Zahlen beider Seiten, ComeFit-Buchungen des Kontos, Kanzlei-Lage, Ursache (als Vermutung gekennzeichnet) und Notiz
export function KontoDetail({ z, ym, nz, neuLaden, setFehler, setHinweis }) {
  const aktion = useAktion(setFehler, setHinweis);
  const [bu, setBu] = useState(null);
  const [f, setF] = useState({ status: nz?.status || "offen", ursache: nz?.ursache || "", notiz: nz?.notiz || "" });
  const ende = new Date(Math.floor(ym / 100), ym % 100, 0).getDate();
  useEffect(() => {
    if (z.art === "bwa") return;
    pbApi.buchungen({ von: `${ymWert(ym)}-01`, bis: `${ymWert(ym)}-${ende}`, konto: z.schluessel, limit: 200 }).then(setBu).catch(e => setFehler(e.message));
  }, [z.art, z.schluessel, ym, ende, setFehler]);
  const s = STATUS[z.status] || { text: z.status };
  const zeile = (t, a, b) => <div style={{ display: "grid", gridTemplateColumns: "minmax(110px, 160px) 1fr 1fr", gap: 6, padding: "3px 0" }}><span style={{ color: THEME.colors.textMuted }}>{t}</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{a}</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{b}</span></div>;
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div>
        <strong>{z.schluessel} {z.bezeichnung || ""}</strong> <Chip text={s.text} farbe={s.farbe} />
        {z.klasse !== "vergleichbar" && <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Einordnung: {KLASSE[z.klasse] || z.klasse}{z.hinweis ? ` – ${z.hinweis}` : ""}</div>}
        {z.klasse === "vergleichbar" && z.hinweis && <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 4 }}>💭 {z.hinweis}</div>}
      </div>
      <div>
        {zeile("", <b>ComeFit</b>, <b>Kanzlei</b>)}
        {z.art === "konto" && <>{zeile("Soll im Monat", geld(z.pb_soll), z.kanzlei_soll === null || z.kanzlei_soll === undefined ? "–" : geld(z.kanzlei_soll))}{zeile("Haben im Monat", geld(z.pb_haben), z.kanzlei_haben === null || z.kanzlei_haben === undefined ? "–" : geld(z.kanzlei_haben))}</>}
        {zeile(z.art === "saldo" ? "Saldo" : "Bewegung (Soll − Haben)", geld(z.pb_wert), z.kanzlei_wert === null ? "–" : geld(z.kanzlei_wert))}
        {zeile("Differenz", <b style={{ color: z.differenz && Number(z.differenz) ? THEME.colors.danger : undefined }}>{geld(z.differenz)}</b>, "")}
        {Number(z.unsicher_anteil) > 0 && <div style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, marginTop: 4 }}>ComeFit-Seite enthält {geld(z.unsicher_anteil)} aus UNSICHEREN Buchungen.</div>}
        {z.qualitaet && z.qualitaet !== "eindeutig" && <div style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, marginTop: 4 }}>Qualität der Kanzlei-Zeile: {z.qualitaet}</div>}
      </div>
      {z.art !== "bwa" && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Buchungen der Parallelbuchhaltung auf diesem Konto im Monat</div>
          {!bu && <div style={{ color: THEME.colors.textMuted }}>Lädt…</div>}
          {bu && bu.buchungen.length === 0 && <div style={{ color: THEME.colors.textMuted }}>Keine Buchungen der Parallelbuchhaltung auf diesem Konto.</div>}
          {bu && bu.buchungen.map(b => (
            <div key={b.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", padding: "4px 0", borderTop: `1px solid ${THEME.colors.border}`, fontSize: THEME.fontSize.base }}>
              <span>{new Date(b.datum).toLocaleDateString("de-DE")} · {b.buchungstext || "–"} {b.belegfeld ? `· Beleg ${b.belegfeld}` : ""}</span>
              <span>{b.zeilen.filter(x => x.konto === z.schluessel).map(x => (Number(x.soll) ? `Soll ${geld(x.soll)}` : `Haben ${geld(x.haben)}`)).join(", ")} · <b style={{ color: b.sicherheit === "unsicher" ? THEME.colors.danger : THEME.colors.textSecondary }}>{b.sicherheit === "sicher" ? "belegt" : b.sicherheit}</b></span>
              {b.hinweis && <span style={{ flexBasis: "100%", color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>ℹ️ {b.hinweis}</span>}
            </div>
          ))}
          <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 6 }}>Einzelbuchungen der Kanzlei liegen nicht vor – importiert werden nur die Monatssummen aus der Summen- und Saldenliste. Für eine Einzelklärung bitte Kontenblatt/Journal beim Steuerberater anfordern.</div>
        </div>
      )}
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Abweichung nachvollziehen (Notiz)</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
          <select aria-label="Status" style={fs.input} value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
            <option value="offen">offen</option><option value="erklaert">erklärt</option><option value="an_kanzlei">an Kanzlei zu klären</option><option value="kein_fehler">kein Fehler</option>
          </select>
          <select aria-label="Ursache" style={fs.input} value={f.ursache} onChange={e => setF({ ...f, ursache: e.target.value })}><option value="">Ursache wählen…</option>{Object.entries(URSACHE).map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select>
        </div>
        <textarea aria-label="Begründung" style={{ ...fs.input, width: "100%", marginTop: 8, minHeight: 56 }} placeholder="Begründung / Notiz (bei „erklärt“ und „kein Fehler“ Pflicht)" value={f.notiz} onChange={e => setF({ ...f, notiz: e.target.value })} />
        <button style={{ ...fs.btn, marginTop: 8 }} onClick={() => aktion(() => pbApi.notiz({ ym, art: z.art, schluessel: z.schluessel, status: f.status, ursache: f.ursache || null, notiz: f.notiz }), "Notiz gespeichert.").then(neuLaden)}>Notiz speichern</button>
      </div>
    </div>
  );
}

// Kontoklassifikation (was ist vergleichbar?) - unveraendert in der Funktion, nur neu platziert
function KlassifikationAbschnitt({ neuLaden, setFehler, setHinweis }) {
  const aktion = useAktion(setFehler, setHinweis);
  const [klass, setKlass] = useState(null);
  return (
    <Abschnitt titel="Kontoklassifikation (was ist vergleichbar?)" rechts={<button style={fs.btnGray} onClick={() => (klass ? setKlass(null) : pbApi.klassifikation().then(setKlass).catch(e => setFehler(e.message)))}>{klass ? "Ausblenden" : "Anzeigen"}</button>}>
      <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>Personen-/Sammelkonten und Konten, die bei der Kanzlei bleiben (Lohn, Abschreibung, Abschluss, steuerliche Sonderbuchungen), sind „nicht vergleichbar“ – mit Begründung und Differenz sichtbar. Vorschläge sind unbestätigt.</div>
      {klass && (
        <div style={{ marginTop: 8 }}>
          <button style={fs.btnGray} onClick={() => aktion(() => pbApi.klassVorschlaege(), r => `${r.neu} Vorschläge angelegt (unbestätigt).`).then(() => pbApi.klassifikation().then(setKlass))}>Vorschläge anlegen</button>
          {klass.map(r => (
            <div key={r.id} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "6px 0", borderTop: `1px solid ${THEME.colors.border}` }}>
              <strong style={{ minWidth: 110 }}>{r.konto_von}{r.konto_bis !== r.konto_von ? `–${r.konto_bis}` : ""}</strong>
              <Chip text={KLASSE[r.typ] || r.typ} farbe={THEME.colors.accent} />
              <span style={{ flex: 1, minWidth: 180, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{r.grund}</span>
              <Chip text={r.bestaetigt ? "bestätigt" : "unbestätigt"} farbe={r.bestaetigt ? THEME.colors.success : THEME.colors.warning} />
              {!r.bestaetigt && <button style={fs.btnGray} onClick={() => aktion(() => pbApi.klassSetzen({ konto_von: r.konto_von, konto_bis: r.konto_bis, typ: r.typ, grund: r.grund, bestaetigt: true })).then(() => { pbApi.klassifikation().then(setKlass); neuLaden(); })}>Bestätigen</button>}
            </div>
          ))}
        </div>
      )}
    </Abschnitt>
  );
}
